import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ClipboardEdit } from "lucide-react";
import {
  type AuditFinding, type FindingSeverity, type FindingStatus, type CompanyProfile,
} from "@/hooks/use-audit-findings";

interface UpdateFindingModalProps {
  open: boolean;
  onClose: () => void;
  finding: AuditFinding | null;
  profiles: CompanyProfile[];
  onSubmit: (id: string, updates: {
    status: FindingStatus;
    notes: string;
    assignedTo: string | null;
    immediateAction: string;
    correctiveAction: string;
  }) => void;
}

const statusOptions: { value: FindingStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
];

const severityBadgeColor: Record<FindingSeverity, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high:     'bg-orange-100 text-orange-800 border-orange-200',
  medium:   'bg-yellow-100 text-yellow-800 border-yellow-200',
  low:      'bg-blue-100 text-blue-800 border-blue-200',
};

export function UpdateFindingModal({ open, onClose, finding, profiles, onSubmit }: UpdateFindingModalProps) {
  const [status, setStatus] = useState<FindingStatus>('open');
  const [notes, setNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [immediateAction, setImmediateAction] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');

  useEffect(() => {
    if (finding) {
      setStatus(finding.status);
      setNotes(finding.notes || '');
      setAssignedTo(finding.assigned_to || '');
      setImmediateAction(finding.immediate_action || '');
      setCorrectiveAction(finding.corrective_action || '');
    }
  }, [finding]);

  const handleSubmit = () => {
    if (!finding) return;
    onSubmit(finding.id, {
      status,
      notes: notes.trim(),
      assignedTo: assignedTo || null,
      immediateAction: immediateAction.trim(),
      correctiveAction: correctiveAction.trim(),
    });
    onClose();
  };

  if (!finding) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[580px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardEdit className="h-5 w-5 text-blue-500" />
            Update Finding
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Finding summary */}
          <div className="px-3 py-2 rounded-md border bg-muted/40 space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-muted-foreground">{finding.id.slice(0, 8)}</span>
              <Badge className={`text-xs ${severityBadgeColor[finding.severity]}`} variant="outline">
                {finding.severity.charAt(0).toUpperCase() + finding.severity.slice(1)}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">{finding.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="finding-status">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as FindingStatus)}>
                <SelectTrigger id="finding-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="finding-assigned">Assigned To</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger id="finding-assigned">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="immediate-action">Immediate Action</Label>
            <Textarea
              id="immediate-action"
              placeholder="Quick steps taken to reduce risk right now — before the full fix..."
              value={immediateAction}
              onChange={(e) => setImmediateAction(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="corrective-action">Corrective & Preventive Action (CAPA)</Label>
            <Textarea
              id="corrective-action"
              placeholder="Full remediation plan — what will be done, by whom, and by when to prevent recurrence..."
              value={correctiveAction}
              onChange={(e) => setCorrectiveAction(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="finding-notes">Notes</Label>
            <Textarea
              id="finding-notes"
              placeholder="Any additional context or updates..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
